import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import CloseIcon from '@material-ui/icons/Close';
import axios from 'axios';
import PropTypes from 'prop-types';
import { storeContext } from '../provider/Provider';

const { REACT_APP_FLASK_API_URL } = process.env;

const useStyles = makeStyles(() => ({
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#58595B',
    marginBottom: 8,
  },
  addRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    marginTop: 8,
  },
}));

const GeoVisibilityConfig = ({ open, onClose }) => {
  const classes = useStyles();
  const { state } = useContext(storeContext);
  const domain = state.websiteSwitch.currentWebsite;

  // Shared lists
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [locations, setLocations] = useState([]);
  const [promptTemplates, setPromptTemplates] = useState([]);

  // Website assignments
  const [assignedPAIds, setAssignedPAIds] = useState(new Set());
  const [assignedLocIds, setAssignedLocIds] = useState(new Set());
  const [assignedTemplateIds, setAssignedTemplateIds] = useState(new Set());

  // Custom questions (per-website)
  const [customQuestions, setCustomQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');

  // New item forms
  const [newPA, setNewPA] = useState({ name: '' });
  const [newLoc, setNewLoc] = useState({ name: '', lat: '', lng: '', state: '' });
  const [newTemplate, setNewTemplate] = useState({ template: '', label: '' });
  const [geocoding, setGeocoding] = useState(false);

  const [saving, setSaving] = useState(false);

  // Fetch all data on open
  useEffect(() => {
    if (!open) return;

    axios
      .get(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/practice-areas`)
      .then((res) => {
        if (!res.data.error) setPracticeAreas(res.data.data);
      })
      .catch(() => {});

    axios
      .get(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/locations`)
      .then((res) => {
        if (!res.data.error) setLocations(res.data.data);
      })
      .catch(() => {});

    axios
      .get(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/prompt-templates`)
      .then((res) => {
        if (!res.data.error) setPromptTemplates(res.data.data);
      })
      .catch(() => {});

    if (domain) {
      axios
        .get(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/${domain}/config`)
        .then((res) => {
          if (!res.data.error) {
            setAssignedPAIds(new Set(res.data.data.practice_areas.map((pa) => pa.id)));
            setAssignedLocIds(new Set(res.data.data.locations.map((loc) => loc.id)));
            setAssignedTemplateIds(new Set((res.data.data.prompt_templates || []).map((t) => t.id)));
            setCustomQuestions((res.data.data.custom_questions || []).map((q) => ({ id: q.id, question: q.question })));
          }
        })
        .catch(() => {});

      axios
        .get(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/${domain}/custom-questions`)
        .then((res) => {
          if (!res.data.error) setCustomQuestions(res.data.data);
        })
        .catch(() => {});
    }
  }, [open, domain]);

  // Add practice area
  const handleAddPA = () => {
    if (!newPA.name.trim()) return;
    axios
      .post(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/practice-areas`, {
        name: newPA.name.trim(),
      })
      .then((res) => {
        if (!res.data.error) {
          setPracticeAreas((prev) => [...prev, res.data.data]);
          setNewPA({ name: '' });
        }
      })
      .catch(() => {});
  };

  // Delete practice area
  const handleDeletePA = (id) => {
    axios
      .delete(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/practice-areas/${id}`)
      .then((res) => {
        if (!res.data.error) {
          setPracticeAreas((prev) => prev.filter((pa) => pa.id !== id));
          setAssignedPAIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
      })
      .catch(() => {});
  };

  // Geocode city name to get coordinates
  const handleGeocode = () => {
    const cityName = newLoc.name.trim();
    if (!cityName) return;

    const query = newLoc.state
      ? `${cityName}, ${newLoc.state.trim()}, USA`
      : `${cityName}, USA`;

    setGeocoding(true);
    axios
      .get('https://nominatim.openstreetmap.org/search', {
        params: { q: query, format: 'json', limit: 1 },
        headers: { 'Accept-Language': 'en' },
      })
      .then((res) => {
        if (res.data && res.data.length > 0) {
          const result = res.data[0];
          const displayParts = result.display_name.split(',').map((s) => s.trim());
          // Try to extract state from display name (usually 3rd or 4th part)
          const stateCandidate = displayParts.find(
            (part) => part.length > 2 && !part.match(/^\d/) && part !== 'United States'
              && part !== cityName && !part.match(/County$/)
          );

          setNewLoc((prev) => ({
            ...prev,
            lat: parseFloat(result.lat).toFixed(4),
            lng: parseFloat(result.lon).toFixed(4),
            state: prev.state || stateCandidate || '',
          }));
        }
      })
      .catch(() => {})
      .finally(() => setGeocoding(false));
  };

  // Add location
  const handleAddLoc = () => {
    if (!newLoc.name.trim() || !newLoc.lat || !newLoc.lng) return;
    axios
      .post(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/locations`, {
        name: newLoc.name.trim(),
        lat: parseFloat(newLoc.lat),
        lng: parseFloat(newLoc.lng),
        state: newLoc.state.trim() || null,
      })
      .then((res) => {
        if (!res.data.error) {
          setLocations((prev) => [...prev, res.data.data]);
          setNewLoc({ name: '', lat: '', lng: '', state: '' });
        }
      })
      .catch(() => {});
  };

  // Delete location
  const handleDeleteLoc = (id) => {
    axios
      .delete(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/locations/${id}`)
      .then((res) => {
        if (!res.data.error) {
          setLocations((prev) => prev.filter((loc) => loc.id !== id));
          setAssignedLocIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
      })
      .catch(() => {});
  };

  // Toggle assignment
  const togglePA = (id) => {
    setAssignedPAIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleLoc = (id) => {
    setAssignedLocIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleTemplate = (id) => {
    setAssignedTemplateIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Prompt template CRUD
  const handleAddTemplate = () => {
    if (!newTemplate.template.trim()) return;
    axios
      .post(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/prompt-templates`, {
        template: newTemplate.template.trim(),
        label: newTemplate.label.trim() || null,
      })
      .then((res) => {
        if (!res.data.error) {
          setPromptTemplates((prev) => [...prev, res.data.data]);
          setNewTemplate({ template: '', label: '' });
        }
      })
      .catch(() => {});
  };

  const handleDeleteTemplate = (id) => {
    axios
      .delete(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/prompt-templates/${id}`)
      .then((res) => {
        if (!res.data.error) {
          setPromptTemplates((prev) => prev.filter((t) => t.id !== id));
          setAssignedTemplateIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
      })
      .catch(() => {});
  };

  // Custom question CRUD
  const handleAddQuestion = () => {
    if (!newQuestion.trim() || !domain) return;
    axios
      .post(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/${domain}/custom-questions`, {
        question: newQuestion.trim(),
      })
      .then((res) => {
        if (!res.data.error) {
          setCustomQuestions((prev) => [...prev, res.data.data]);
          setNewQuestion('');
        }
      })
      .catch(() => {});
  };

  const handleDeleteQuestion = (id) => {
    if (!domain) return;
    axios
      .delete(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/${domain}/custom-questions/${id}`)
      .then((res) => {
        if (!res.data.error) {
          setCustomQuestions((prev) => prev.filter((q) => q.id !== id));
        }
      })
      .catch(() => {});
  };

  // Save assignments
  const handleSave = () => {
    if (!domain) return;
    setSaving(true);
    axios
      .put(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/${domain}/config`, {
        practice_area_ids: Array.from(assignedPAIds),
        location_ids: Array.from(assignedLocIds),
        prompt_template_ids: Array.from(assignedTemplateIds),
      })
      .then(() => {
        onClose();
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <span>AI Visibility Configuration</span>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {domain && (
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            Configuring for: <strong>{domain}</strong>. Check the items to assign to this website.
            Unchecked items will still be available for other websites.
          </Typography>
        )}

        {/* Practice Areas */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>Practice Areas</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                {domain && <TableCell padding="checkbox">Assign</TableCell>}
                <TableCell style={{ fontWeight: 700, fontSize: 11 }}>Name</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: 11 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {practiceAreas.map((pa) => (
                <TableRow key={pa.id} hover>
                  {domain && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={assignedPAIds.has(pa.id)}
                        onChange={() => togglePA(pa.id)}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                  )}
                  <TableCell>{pa.name}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleDeletePA(pa.id)}>
                      <DeleteIcon fontSize="small" style={{ color: '#ef4444' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className={classes.addRow}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Practice area name"
              value={newPA.name}
              onChange={(e) => setNewPA({ ...newPA, name: e.target.value })}
              style={{ flex: 1 }}
              inputProps={{ style: { fontSize: 13 } }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddPA}
              disabled={!newPA.name.trim()}
              style={{ background: '#0d9373', color: '#fff', textTransform: 'none' }}
            >
              Add
            </Button>
          </div>
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {/* Locations */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>Locations</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                {domain && <TableCell padding="checkbox">Assign</TableCell>}
                <TableCell style={{ fontWeight: 700, fontSize: 11 }}>City</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: 11 }}>State</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: 11 }}>Lat</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: 11 }}>Lng</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: 11 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {locations.map((loc) => (
                <TableRow key={loc.id} hover>
                  {domain && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={assignedLocIds.has(loc.id)}
                        onChange={() => toggleLoc(loc.id)}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                  )}
                  <TableCell>{loc.name}</TableCell>
                  <TableCell style={{ color: '#666', fontSize: 12 }}>{loc.state || '\u2014'}</TableCell>
                  <TableCell style={{ fontFamily: 'monospace', fontSize: 11 }}>{loc.lat}</TableCell>
                  <TableCell style={{ fontFamily: 'monospace', fontSize: 11 }}>{loc.lng}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleDeleteLoc(loc.id)}>
                      <DeleteIcon fontSize="small" style={{ color: '#ef4444' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className={classes.addRow}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="City name"
              value={newLoc.name}
              onChange={(e) => setNewLoc({ ...newLoc, name: e.target.value })}
              style={{ flex: 1 }}
              inputProps={{ style: { fontSize: 13 } }}
            />
            <TextField
              size="small"
              variant="outlined"
              placeholder="State"
              value={newLoc.state}
              onChange={(e) => setNewLoc({ ...newLoc, state: e.target.value })}
              style={{ width: 100 }}
              inputProps={{ style: { fontSize: 13 } }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleGeocode}
              disabled={!newLoc.name.trim() || geocoding}
              style={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              {geocoding ? 'Looking up...' : 'Lookup Coords'}
            </Button>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Lat"
              value={newLoc.lat}
              onChange={(e) => setNewLoc({ ...newLoc, lat: e.target.value })}
              style={{ width: 90 }}
              inputProps={{ style: { fontSize: 13, fontFamily: 'monospace' } }}
            />
            <TextField
              size="small"
              variant="outlined"
              placeholder="Lng"
              value={newLoc.lng}
              onChange={(e) => setNewLoc({ ...newLoc, lng: e.target.value })}
              style={{ width: 90 }}
              inputProps={{ style: { fontSize: 13, fontFamily: 'monospace' } }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddLoc}
              disabled={!newLoc.name.trim() || !newLoc.lat || !newLoc.lng}
              style={{ background: '#0d9373', color: '#fff', textTransform: 'none' }}
            >
              Add
            </Button>
          </div>
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {/* Prompt Templates */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>Prompt Templates</Typography>
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
            Use &#123;practice_area&#125; and &#123;city&#125; as placeholders. Each template generates questions for all practice area &times; location combinations.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                {domain && <TableCell padding="checkbox">Assign</TableCell>}
                <TableCell style={{ fontWeight: 700, fontSize: 11 }}>Template</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: 11 }}>Label</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: 11 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {promptTemplates.map((t) => (
                <TableRow key={t.id} hover>
                  {domain && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={assignedTemplateIds.has(t.id)}
                        onChange={() => toggleTemplate(t.id)}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                  )}
                  <TableCell style={{ fontSize: 12, maxWidth: 350 }}>
                    <span style={{ wordBreak: 'break-word' }}>{t.template}</span>
                    {t.is_default && (
                      <span style={{ marginLeft: 6, fontSize: 10, background: '#e8f5e9', color: '#0d9373', padding: '1px 4px', borderRadius: 3 }}>
                        default
                      </span>
                    )}
                  </TableCell>
                  <TableCell style={{ fontSize: 12 }}>{t.label || '\u2014'}</TableCell>
                  <TableCell align="right">
                    {!t.is_default && (
                      <IconButton size="small" onClick={() => handleDeleteTemplate(t.id)}>
                        <DeleteIcon fontSize="small" style={{ color: '#ef4444' }} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className={classes.addRow} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Template text, e.g. Recommend a {practice_area} law firm in {city}"
              value={newTemplate.template}
              onChange={(e) => setNewTemplate({ ...newTemplate, template: e.target.value })}
              multiline
              rows={2}
              inputProps={{ style: { fontSize: 13 } }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Label (optional)"
                value={newTemplate.label}
                onChange={(e) => setNewTemplate({ ...newTemplate, label: e.target.value })}
                style={{ flex: 1 }}
                inputProps={{ style: { fontSize: 13 } }}
              />
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddTemplate}
                disabled={!newTemplate.template.trim()}
                style={{ background: '#0d9373', color: '#fff', textTransform: 'none' }}
              >
                Add Template
              </Button>
            </div>
          </div>
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {/* Custom Questions (per-website) */}
        {domain && (
          <div className={classes.section}>
            <Typography className={classes.sectionTitle}>Custom Questions</Typography>
            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
              Free-form questions specific to {domain}. These are sent to ChatGPT as-is (no placeholders).
            </Typography>
            {customQuestions.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 700, fontSize: 11 }}>Question</TableCell>
                    <TableCell style={{ fontWeight: 700, fontSize: 11 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customQuestions.map((q) => (
                    <TableRow key={q.id} hover>
                      <TableCell style={{ fontSize: 12 }}>{q.question}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleDeleteQuestion(q.id)}>
                          <DeleteIcon fontSize="small" style={{ color: '#ef4444' }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <div className={classes.addRow}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="e.g. Best wrongful death lawyer in Tampa Bay area"
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                style={{ flex: 1 }}
                inputProps={{ style: { fontSize: 13 } }}
              />
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddQuestion}
                disabled={!newQuestion.trim()}
                style={{ background: '#0d9373', color: '#fff', textTransform: 'none' }}
              >
                Add
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions style={{ padding: '8px 24px 16px' }}>
        <Button onClick={onClose} style={{ textTransform: 'none' }}>Cancel</Button>
        {domain && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            style={{ background: '#0d9373', color: '#fff', textTransform: 'none', fontWeight: 600 }}
          >
            {saving ? 'Saving...' : 'Save Assignments'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

GeoVisibilityConfig.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default GeoVisibilityConfig;
